/**
 * Owner: Masa Bayu
 * File: pdf-layout-parse.ts
 * Date:Time: 2026-09-27 10:42
 * Description: PDF layout pages → Document IR via deterministic preprocess (Eyes).
 */

import {
  createQXDocumentLayout,
  createQXDocumentLayoutBlock,
  createQXDocumentLine,
  createQXDocumentInlineRun,
  createQXDocumentPage,
  preprocessLayoutToDocumentIR,
  type QXDocumentIR,
  type QXDocumentLayout,
  type QXDocumentWarning,
} from '@qx/qx-document-ir';
import { QX_PDF_ADAPTER_VERSION, QX_PDF_SOURCE_TYPE } from './pdf-types';

/** Intake layout page — geometry evidence only · no chapter labels. */
export type QXPdfLayoutPageInput = {
  readonly pageNumber: number;
  readonly width: number;
  readonly height: number;
    readonly blocks: readonly {
    /** Existing layout-block id. Preprocess copies it to provenance.sourceObjectId. */
    readonly id?: string;
    readonly kind?:
      | 'text'
      | 'image'
      | 'table'
      | 'note'
      | 'header'
      | 'footer'
      | 'page_number'
      | 'caption'
      | 'other';
    readonly text?: string | null;
    readonly assetId?: string | null;
    readonly bbox?: { readonly x: number; readonly y: number; readonly width: number; readonly height: number } | null;
    readonly lines?: readonly {
      readonly text?: string;
      readonly bbox?: { readonly x: number; readonly y: number; readonly width: number; readonly height: number } | null;
      readonly linePaint?: {
        readonly extension?: 'R11-LINE-01' | 'R11-LINE-02';
        readonly wordStarts?: readonly number[];
        readonly wordGaps?: readonly number[];
        readonly segments?: readonly {
          readonly text: string;
          readonly x: number;
          readonly width: number;
        }[];
        readonly direction?: 'ltr' | 'rtl' | 'ttb';
        readonly rotation?: number;
        readonly column?: number;
        readonly script?: 'latin' | 'arabic' | 'cjk' | 'mixed' | 'other';
        readonly role?: string;
      };
      readonly runs?: readonly {
        readonly text: string;
        readonly fontName?: string | null;
        readonly fontSize?: number | null;
        readonly bold?: boolean;
        readonly italic?: boolean;
        readonly bbox?: { readonly x: number; readonly y: number; readonly width: number; readonly height: number } | null;
      }[];
    }[];
  }[];
};

export type QXPdfLayoutResolve = (sourceRef: string) => readonly QXPdfLayoutPageInput[] | null;

export function buildLayoutFromPdfPages(
  pages: readonly QXPdfLayoutPageInput[],
): QXDocumentLayout {
  return createQXDocumentLayout({
    pages: pages.map((page) =>
      createQXDocumentPage({
        pageNumber: page.pageNumber,
        width: page.width,
        height: page.height,
        blocks: page.blocks.map((block) => {
          const lines =
            block.lines && block.lines.length > 0
              ? block.lines.map((line) =>
                  createQXDocumentLine({
                    bbox: line.bbox ?? null,
                    runs:
                      line.runs && line.runs.length > 0
                        ? line.runs.map((run) =>
                            createQXDocumentInlineRun({
                              text: run.text,
                              bbox: run.bbox ?? null,
                              style: {
                                fontName: run.fontName ?? null,
                                fontSize: run.fontSize ?? null,
                                bold: run.bold === true,
                                italic: run.italic === true,
                              },
                            }),
                          )
                        : [
                            createQXDocumentInlineRun({
                              text: line.text ?? '',
                            }),
                          ],
                    ...(line.linePaint !== undefined ? { linePaint: line.linePaint } : {}),
                  }),
                )
              : block.kind === 'image' || !block.text
                ? []
                : [
                    createQXDocumentLine({
                      runs: [createQXDocumentInlineRun({ text: block.text })],
                    }),
                  ];
          return createQXDocumentLayoutBlock({
            ...(block.id !== undefined ? { layoutBlockId: block.id } : {}),
            kind: block.kind ?? 'text',
            bbox: block.bbox ?? null,
            text: block.text ?? null,
            assetId: block.assetId ?? null,
            lines,
            provenance: {
              sourceType: QX_PDF_SOURCE_TYPE,
              sourcePage: page.pageNumber,
              adapterVersion: QX_PDF_ADAPTER_VERSION,
              bbox: block.bbox
                ? [block.bbox.x, block.bbox.y, block.bbox.width, block.bbox.height]
                : null,
            },
          });
        }),
      }),
    ),
  });
}

export function parsePdfLayoutToIR(input: {
  readonly sourceHash: string;
  readonly pages: readonly QXPdfLayoutPageInput[];
  readonly extraWarnings?: readonly QXDocumentWarning[];
}): QXDocumentIR {
  if (input.pages.length === 0) {
    throw new Error('PDFAdapter layout extract: empty pages');
  }
  const layout = buildLayoutFromPdfPages(input.pages);
  return preprocessLayoutToDocumentIR({
    sourceType: QX_PDF_SOURCE_TYPE,
    sourceHash: input.sourceHash,
    adapterVersion: QX_PDF_ADAPTER_VERSION,
    layout,
    ...(input.extraWarnings !== undefined && input.extraWarnings.length > 0
      ? { extraWarnings: input.extraWarnings }
      : {}),
  });
}
